'use client';

// ═══════════════════════════════════════════════════════════════
// useThemePath Hook
// Tenant-aware URL generation for theme components
// ═══════════════════════════════════════════════════════════════

import { useCallback } from 'react';
import { useThemeContext } from '../data-provider';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface UseThemePathReturn {
    /** Base path (e.g., "/store/salah" or "") */
    basePath: string;

    /** Generate a tenant-aware link */
    getLink: (path: string) => string;

    /** Generate product link */
    productLink: (slug: string) => string;

    /** Generate collection link */
    collectionLink: (slug?: string) => string;

    /** Generate cart link */
    cartLink: () => string;

    /** Generate checkout link */
    checkoutLink: () => string;

    /** Generate search link */
    searchLink: (query?: string) => string;

    /** Home link */
    homeLink: () => string;
}

// ─────────────────────────────────────────────────────────────
// Hook Implementation
// ─────────────────────────────────────────────────────────────

/**
 * Hook for generating tenant-aware URLs
 * All links in the theme should use this hook
 * 
 * @example
 * const { productLink, getLink } = useThemePath();
 * <Link href={productLink('my-product')}>View Product</Link>
 * <Link href={getLink('/about')}>About Us</Link>
 */
export function useThemePath(): UseThemePathReturn {
    const { basePath = '' } = useThemeContext();

    /**
     * Generate a tenant-aware link from a path
     * Handles leading slashes and prevents double slashes
     */
    const getLink = useCallback((path: string): string => {
        // Ensure path starts with /
        const cleanPath = path.startsWith('/') ? path : `/${path}`;

        // Combine basePath and path, avoiding double slashes
        if (!basePath) {
            return cleanPath;
        }

        // Remove trailing slash from basePath if exists
        const cleanBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

        return `${cleanBasePath}${cleanPath}`;
    }, [basePath]);

    /**
     * Generate product page link
     */
    const productLink = useCallback((slug: string): string => {
        return getLink(`/product/${slug}`);
    }, [getLink]);

    /**
     * Generate collection page link
     */
    const collectionLink = useCallback((slug?: string): string => {
        if (slug) {
            return getLink(`/collection/${slug}`);
        }
        return getLink('/collection');
    }, [getLink]);

    /**
     * Generate cart page link
     */
    const cartLink = useCallback((): string => {
        return getLink('/cart');
    }, [getLink]);

    /**
     * Generate checkout page link
     */
    const checkoutLink = useCallback((): string => {
        return getLink('/checkout');
    }, [getLink]);

    /**
     * Generate search page link
     */
    const searchLink = useCallback((query?: string): string => {
        if (query) {
            return getLink(`/search?q=${encodeURIComponent(query)}`);
        }
        return getLink('/search');
    }, [getLink]);

    /**
     * Home page link
     */
    const homeLink = useCallback((): string => {
        return getLink('/');
    }, [getLink]);

    return {
        basePath,
        getLink,
        productLink,
        collectionLink,
        cartLink,
        checkoutLink,
        searchLink,
        homeLink,
    };
}

export default useThemePath;
